import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import styles from './LoginPage.module.scss';

const ResetPasswordPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const canSubmit = useMemo(() => {
    return (
      normalizedEmail.length > 0 &&
      otp.trim().length > 0 &&
      newPassword.length >= 8 &&
      newPassword === confirmPassword
    );
  }, [normalizedEmail, otp, newPassword, confirmPassword]);

  useEffect(() => {
    if (!error && !message) return;
    const timer = setTimeout(() => {
      setError(null);
      setMessage(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [error, message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await authService.resetPassword({
        email: normalizedEmail,
        otp: otp.trim(),
        newPassword,
      });
      setMessage(res.message || 'Password has been reset successfully.');
      setTimeout(() => navigate('/?auth=login'), 600);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!normalizedEmail) {
      return;
    }
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const res = await authService.requestPasswordReset({ email: normalizedEmail });
      setMessage(res.message || 'If that email is registered, we have sent a new OTP.');
    } catch (err: any) {
      setError(err?.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.authLayout}>
      <div className={styles.authLeft}>
        <div className={styles.authLogoSection} />
        <div className={styles.authBenefits}>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>📩</span>
            <div>
              <h3 className={styles.benefitItemH3}>Check your inbox</h3>
              <p className={styles.benefitItemP}>Use the 6-digit OTP sent to your email.</p>
            </div>
          </div>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>🔒</span>
            <div>
              <h3 className={styles.benefitItemH3}>Create a new password</h3>
              <p className={styles.benefitItemP}>Use a strong password you don’t reuse elsewhere.</p>
            </div>
          </div>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>♻️</span>
            <div>
              <h3 className={styles.benefitItemH3}>Didn’t get a code?</h3>
              <p className={styles.benefitItemP}>You can request a new OTP anytime.</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.authRight}>
        <div className={styles.authFormWrapper}>
          <div className={styles.themeToggleContainer}>
            <button
              className={styles.themeToggleBtn}
              onClick={toggleTheme}
              aria-label="Toggle theme"
              type="button"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>

          <img
            src={`${process.env.PUBLIC_URL || ''}/arihex-logo-${theme === 'dark' ? 'dark' : 'light'}.png`}
            alt="Arihex"
            className={styles.authLogoImg}
          />
          <h2 className={styles.authFormTitle}>Reset password</h2>
          <p className={styles.authFormSubtitle}>Enter your OTP and choose a new password.</p>

          {error && <div className="app-error">{error}</div>}
          {message && <div className="app-success">{message}</div>}

          <form className={styles.authForm} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="email">Email Address</label>
              <div className={styles.formInputWrapper}>
                <span className={styles.formIcon}>✉️</span>
                <input
                  id="email"
                  type="email"
                  className={styles.formInput}
                  placeholder="john@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="otp">OTP</label>
              <div className={styles.formInputWrapper}>
                <span className={styles.formIcon}>🔢</span>
                <input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={styles.formInput}
                  placeholder="6-digit code"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="newPassword">New Password</label>
              <div className={styles.formInputWrapper}>
                <span className={styles.formIcon}>🔒</span>
                <input
                  id="newPassword"
                  type="password"
                  className={styles.formInput}
                  placeholder="••••••••"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="confirmPassword">Confirm Password</label>
              <div className={styles.formInputWrapper}>
                <span className={styles.formIcon}>✅</span>
                <input
                  id="confirmPassword"
                  type="password"
                  className={styles.formInput}
                  placeholder="••••••••"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className={`${styles.btnFull} btn btn-primary`}
              disabled={loading || !canSubmit}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className={styles.authToggle}>
            <span>
              Need a new OTP?{' '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleResendOtp}
                disabled={loading || resendLoading || !normalizedEmail}
              >
                {resendLoading ? 'Sending...' : 'Resend OTP'}
              </button>
            </span>
          </div>

          <div className={styles.authFooter}>
            <span>
              Back to{' '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => navigate('/?auth=login')}
                disabled={loading}
              >
                Sign in
              </button>
            </span>
          </div>
        </div>
      </div>

      <div className={styles.authFooterInner}>
        <footer className={styles.authFooterBar}>
          <div className={styles.authFooterContent}>
            <p>© 2026 arihex.com All rights reserved.</p>
            <div className={styles.authFooterLinks}>
              <Link to="/about">About</Link>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/contact">Contact Support</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

